import './App.css';
import './styles/ReleaseFixes.css';
import './styles/Polish.css';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import LanguageSwitcher from './components/LanguageSwitcher';
import { LanguageProvider, useLanguage, SUPPORTED_LANGUAGES, languageFromPath, localePath } from './i18n/i18n';
import Contact from './components/Contact';
import { syncSeoHead } from './i18n/seo.mjs';
import NoAccountNeeded from './components/NoAccountNeeded';
import PerformanceMonitor from './components/PerformanceMonitor';

const NewHome = lazy(() => import('./components/NewHome'));
const Home = lazy(() => import('./components/Home'));
const Welcome = lazy(() => import('./components/Welcome'));
const PhotoBooth = lazy(() => import('./components/PhotoBooth'));
const PhotoPreview = lazy(() => import('./components/PhotoPreview'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Tutorial = lazy(() => import('./components/Tutorial'));
const PhotoTutorial = lazy(() => import('./components/PhotoTutorial'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const SharePage = lazy(() => import('./components/SharePage'));
const Gallery = lazy(() => import('./components/Gallery'));
const GalleryDetail = lazy(() => import('./components/GalleryDetail'));
const GalleryAdmin = lazy(() => import('./components/GalleryAdmin'));

function LoadingSpinner() {
  const { t } = useLanguage();
  return <div className="release-loading" role="status">{t('loading')}</div>;
}

function RetakeRoute({ capturedImages, setCapturedImages, editorOptions, setEditorOptions }) {
  const { index } = useParams();
  const { pathname } = useLocation();
  const language = languageFromPath(pathname);
  const position = /^\d+$/.test(index || '') ? Number(index) : -1;
  // A URL alone is not a photo session. Never open the camera for an invalid slot.
  if (!Number.isSafeInteger(position) || position < 0 || position >= capturedImages.length) {
    return <Navigate replace to={localePath(language, '/photobooth')} />;
  }
  return <PhotoBooth editorOptions={editorOptions} setEditorOptions={setEditorOptions} key={`${language}-${position}`} setCapturedImages={setCapturedImages} existingImages={capturedImages} retakeIndex={position} />;
}

function AppContent() {
  const [capturedImages, setCapturedImages] = useState([]);
  const [editorOptions, setEditorOptions] = useState({ layoutStyle: 'classic', stripColor: 'white', selectedFrame: 'none' });
  const [showTutorial, setShowTutorial] = useState(false);
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { document.getElementById('loading-overlay')?.remove(); }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = event => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  useEffect(() => {
    const next = languageFromPath(location.pathname);
    if (language !== next) changeLanguage(next);
  }, [location.pathname, language, changeLanguage]);
  useEffect(() => {
    const path = location.pathname.replace(/^\/(en|zh|id|vi|pt|ja|th|es|it)(?=\/|$)/, '') || '/';
    const titles = { '/': language === 'en' ? 'Picapica Booth — Free Online Photo Booth, No Download' : `${t('appName')} - ${t('tagline')}`, '/photobooth': t('photoBoothTitle'), '/preview': t('previewTitle'), '/privacy-policy': `${t('privacy')} | ${t('appName')}`, '/contact': `${t('contact')} | ${t('appName')}` };
    document.title = titles[path] || `${t('appName')} - ${t('tagline')}`;
    syncSeoHead(location.pathname);
  }, [location.pathname, language, t]);

  const prefixes = ['', ...SUPPORTED_LANGUAGES.map(code => `/${code}`)];
  return <div className="App">
    <PerformanceMonitor />
    <Suspense fallback={<LoadingSpinner />}>
      {showTutorial && <Tutorial onComplete={() => setShowTutorial(false)} />}
    </Suspense>
    <header className="App-header">
      <nav className={`navbar ${menuOpen ? 'nav-open' : ''}`}>
        <div className="nav-left"><Link to={localePath(language, '/')} className="logo-link">
          <div className="logo"><svg width="30" height="30" viewBox="0 0 40 40" fill="none" aria-hidden="true"><rect width="40" height="40" rx="8" fill="#FF6B9D"/><circle cx="20" cy="20" r="8" fill="white"/><circle cx="20" cy="20" r="4" fill="#FF6B9D"/><rect x="24" y="10" width="2" height="4" rx="1" fill="white"/></svg><span className="brand-name">{t('appName')}</span></div>
        </Link></div>
        <div className="nav-center" id="primary-nav">
          <Link to={localePath(language, '/')}>{t('features')}</Link>
          <Link to={localePath(language, '/gallery')}>{t('gallery')}</Link>
          <Link to={localePath(language, '/privacy-policy')}>{t('privacy')}</Link>
          <Link to={localePath(language, '/contact')}>{t('contact')}</Link>
          <Link to={localePath(language, '/blog')}>{t('blog')}</Link>
        </div>
        <div className="nav-right"><button type="button" className="nav-toggle" aria-expanded={menuOpen} aria-controls="primary-nav" aria-label={t('navMenu')} onClick={() => setMenuOpen(value => !value)}><i className={`fas fa-${menuOpen ? 'times' : 'bars'}`} aria-hidden="true" /></button><LanguageSwitcher onSwitchLanguage={next => {
          const path = location.pathname.replace(/^\/(en|zh|id|vi|pt|ja|th|es|it)(?=\/|$)/, '') || '/';
          navigate(localePath(next, path) + location.search + location.hash);
        }} /></div>
      </nav>
    </header>
    <Suspense fallback={<LoadingSpinner />}><Routes>
      {prefixes.flatMap(prefix => [
        <Route key={`${prefix}/home`} path={prefix || '/'} element={<NewHome setEditorOptions={setEditorOptions} editorOptions={editorOptions} setCapturedImages={setCapturedImages} onShowTutorial={() => setShowTutorial(true)} />} />,
        <Route key={`${prefix}/photobooth`} path={`${prefix}/photobooth`} element={<PhotoBooth editorOptions={editorOptions} setEditorOptions={setEditorOptions} setCapturedImages={setCapturedImages} existingImages={capturedImages} />} />,
        <Route key={`${prefix}/retake`} path={`${prefix}/retake/:index`} element={<RetakeRoute editorOptions={editorOptions} setEditorOptions={setEditorOptions} capturedImages={capturedImages} setCapturedImages={setCapturedImages} />} />,
        <Route key={`${prefix}/preview`} path={`${prefix}/preview`} element={<PhotoPreview capturedImages={capturedImages} editorOptions={editorOptions} setEditorOptions={setEditorOptions} />} />,
        <Route key={`${prefix}/privacy`} path={`${prefix}/privacy-policy`} element={<PrivacyPolicy />} />,
        <Route key={`${prefix}/contact`} path={`${prefix}/contact`} element={<Contact />} />,
        <Route key={`${prefix}/about`} path={`${prefix}/about`} element={<Home onShowTutorial={() => setShowTutorial(true)} />} />,
        <Route key={`${prefix}/welcome`} path={`${prefix}/welcome`} element={<Welcome />} />,
        <Route key={`${prefix}/tutorial`} path={`${prefix}/tutorial`} element={<PhotoTutorial />} />,
        <Route key={`${prefix}/tips`} path={`${prefix}/photo-tutorial`} element={<Navigate to={`${prefix}/tutorial`} replace />} />,
        <Route key={`${prefix}/guide`} path={`${prefix}/user-guide`} element={<Navigate to={`${prefix}/tutorial`} replace />} />,
        <Route key={`${prefix}/blog`} path={`${prefix}/blog`} element={<Blog />} />,
        <Route key={`${prefix}/post`} path={`${prefix}/blog/:slug`} element={<BlogPost />} />,
        <Route key={`${prefix}/gallery`} path={`${prefix}/gallery`} element={<Gallery />} />,
        <Route key={`${prefix}/gallery-detail`} path={`${prefix}/gallery/:id`} element={<GalleryDetail />} />,
        ...['login', 'signup', 'pricing'].map(page => <Route key={`${prefix}/${page}`} path={`${prefix}/${page}`} element={<NoAccountNeeded />} />)
      ])}
      <Route path="/gallery-admin" element={<GalleryAdmin />} />
      <Route path="/share/:shareId" element={<SharePage />} />
    </Routes></Suspense>
  </div>;
}

export default function AppWrapper() {
  return <LanguageProvider><AppContent /></LanguageProvider>;
}
